import { useContext, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Modal, ModalBody, ModalHeader, Row } from "react-bootstrap";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import UserAvatar from "../../../components/UserAvatar";
import PaginationComponent from "../../../components/Pagination";
import Badge from "../../../components/Badge";
import ErrorMessage from "../../../components/ErrorMessage";
import InfoItem from "../../../helpers/InfoItem/InfoItem";
import Currency, { ConvertCurrency } from "../../../helpers/Currency/Currency";
import FieldItem, { FieldDetails } from "../../../components/FieldItem";
import DataExporter from "../../../components/DataExporter";
import DateTimePicker from "../../../components/DateTimePicker";
import { InvoiceDetails } from "../InvoicesUser/InvoicesUser";
import { orderUserContext } from "../../../context/UserContext/OrderUserContext/OrderUserContext";
import { convertDate } from "../../../utils";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import {
  convertInvoiceStatusToColor,
  convertInvoiceStatusToText,
  convertOrderStatusToColor,
  convertOrderStatusToText,
  convertProductTypeToText,
  convertRoleToPath,
  exporterData,
  orderStatusItems,
  productTypeItems,
} from "../../../utils/enum";
import { editOrderValidation } from "../../../utils/validation/validation";

const OrdersUser = () => {
  const navigate = useNavigate();

  const { user, product, invoice } = useParams();

  const auth = useSelector((state) => state.auth);

  const {
    orders,
    order,
    setOrder,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    status,
    setStatus,
    invoiceStatus,
    setInvoiceStatus,
    productType,
    setProductType,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    handleGetOrders,
    handleEditOrder,
    handleDeleteOrder,
  } = useContext(orderUserContext);

  useEffect(() => {
    handleGetOrders();
  }, [
    start,
    end,
    pageNumber,
    status,
    invoiceStatus,
    itemsCount,
    sort,
    productType,
    user,
    product,
    invoice,
  ]);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(editOrderValidation),
  });

  const submitForm = (data) => {
    const requiredFields = order?.product?.fields
      .filter((field) => field.isRequired && !data[field.name])
      .map((field) => field.label);
    if (!isEmpty(requiredFields)) return;

    handleEditOrder(data);
  };

  return (
    <>
      <SiteTitle title="سفارشات" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        orderStatus={status}
        setOrderStatus={setStatus}
        invoiceStatus={invoiceStatus}
        setInvoiceStatus={setInvoiceStatus}
        productType={productType}
        setProductType={setProductType}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          {...(!isEmpty(orders) && {
            endItem: (
              <DataExporter
                headers={exporterData.orders.headers}
                keys={exporterData.orders.keys}
                data={orders}
                fileName={exporterData.orders.fileName}
              />
            ),
          })}
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetOrders}
        />
        <div className="card-body p-24">
          {isEmpty(orders) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">محصول</th>
                      {auth.isAdmin && (
                        <>
                          <th scope="col">کاربر</th>
                          <th scope="col">شماره همراه</th>
                        </>
                      )}
                      <th scope="col">
                        مبلغ <Currency />
                      </th>
                      <th scope="col">کدپیگیری</th>
                      <th scope="col">نوع محصول</th>
                      <th scope="col">تاریخ ثبت</th>
                      <th scope="col">وضعیت فاکتور</th>
                      <th scope="col">وضعیت</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((item, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>
                          <Link
                            to={`/${convertRoleToPath(auth.role)}/product/${
                              item?.product?.original
                            }/product`}
                          >
                            <div className="d-flex align-items-center">
                              <UserAvatar
                                className="ms-2"
                                {...(item?.product?.picture
                                  ? { image: item?.product?.picture }
                                  : {
                                      icon: "solar:box-broken",
                                    })}
                              />
                              <div className="flex-grow-1">
                                <span className="text-md mb-0 fw-normal text-secondary-light">
                                  {item?.product?.title}
                                </span>
                              </div>
                            </div>
                          </Link>
                        </td>
                        {auth.isAdmin && (
                          <>
                            <td>
                              <div className="d-flex align-items-center">
                                <UserAvatar
                                  className="ms-2"
                                  {...(item?.user?.avatar
                                    ? { image: item?.user?.avatar }
                                    : {
                                        icon: "solar:order-broken",
                                      })}
                                />
                                <div className="flex-grow-1">
                                  <span className="text-md mb-0 fw-normal text-secondary-light">
                                    {item?.user?.firstName}{" "}
                                    {item?.user?.lastName}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>{item?.user?.phoneNumber}</td>
                          </>
                        )}
                        <td>
                          <ConvertCurrency
                            price={item?.invoice?.amount}
                            pending
                          />
                        </td>
                        <td>{item?.trackingCode}</td>
                        <td>{convertProductTypeToText(item?.product?.type)}</td>
                        <td>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          <Badge
                            color={convertInvoiceStatusToColor(
                              item?.invoice?.status
                            )}
                          >
                            {convertInvoiceStatusToText(item?.invoice?.status)}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={convertOrderStatusToColor(item.status)}>
                            {convertOrderStatusToText(item.status)}
                          </Badge>
                        </td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="hover-bg-transparent"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <Icon
                                icon="ph:dots-three-outline-fill"
                                className="menu-icon"
                              />
                            </button>
                            <ul className="dropdown-menu list">
                              <li
                                onClick={() =>
                                  navigate(
                                    `/${convertRoleToPath(
                                      auth.role
                                    )}/renewals/${item._id}/order`
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                گزارش تمدیدهای سفارش
                              </li>
                              <li
                                onClick={() => setOrder(item)}
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                جزئیات
                              </li>
                              {auth.isAdmin && (
                                <>
                                  <li
                                    onClick={() => {
                                      setOrder({ ...item, isEdit: true });
                                      reset({
                                        status: item?.status,
                                        renewalDate: item?.renewalDate,
                                        ...item.details,
                                      });
                                    }}
                                    className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                  >
                                    ویرایش
                                  </li>

                                  <li className="dropdown-divider" />

                                  <li
                                    onClick={() =>
                                      areYouSureMessage(
                                        () => handleDeleteOrder(item._id),
                                        `آیا از حذف سفارش ${item?.user?.firstName} ${item?.user?.lastName} مطمئنید؟`
                                      )
                                    }
                                    className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                                  >
                                    حذف
                                  </li>
                                </>
                              )}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={order?.isEdit}
        className="modal-dialog-centered"
        size="xl"
        onHide={() => setOrder({})}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            ویرایش سفارش {order?.user?.firstName} {order?.user?.lastName}
          </span>
        </ModalHeader>
        <ModalBody>
          <form autoComplete="off" onSubmit={handleSubmit(submitForm)}>
            <div className="row">
              <div className="col-12 col-md-6">
                <div className="mb-10">
                  <div>
                    <label
                      htmlFor="status"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      وضعیت سفارش
                    </label>
                    <select
                      className="form-control radius-8"
                      {...register("status")}
                    >
                      <option value="" selected hidden>
                        وضعیت سفارش را انتخاب نمایید
                      </option>
                      {orderStatusItems.map((item) => (
                        <option
                          key={item}
                          value={item}
                          selected={watch("status") === item}
                        >
                          {convertOrderStatusToText(item)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <ErrorMessage message={errors.status?.message} />
                </div>
              </div>
              {!!order?.product?.renewalDate &&
                order?.product?.type !== productTypeItems[0] && (
                  <div className="col-12 col-md-6">
                    <div className="mb-10">
                      <div>
                        <label
                          htmlFor="renewalDate"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          تاریخ تمدید
                        </label>
                        <Controller
                          control={control}
                          render={({ field: { value, onChange, onBlur } }) => (
                            <DateTimePicker
                              id="renewalDate"
                              name="renewalDate"
                              className="form-control radius-8"
                              placeholder="تاریخ تمدید را وارد نمایید"
                              onChange={onChange}
                              onBlur={onBlur}
                              initValue={value}
                            />
                          )}
                          name="renewalDate"
                        />
                      </div>

                      <ErrorMessage message={errors.renewalDate?.message} />
                    </div>
                  </div>
                )}
              {order?.product?.fields?.map((_item) => (
                <FieldItem
                  control={control}
                  watch={watch}
                  item={_item}
                  key={_item._id}
                  isSubmitted={isSubmitted}
                />
              ))}
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                ویرایش
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <Modal
        show={!isEmpty(order) && !order?.isEdit}
        className="modal-dialog-centered"
        size="lg"
        onHide={() => setOrder({})}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">جزئیات عملیات</span>
        </ModalHeader>
        <ModalBody>
          {auth.isAdmin && (
            <>
              <span className="text-primary border-bottom fw-bold">
                اطلاعات کاربر
              </span>
              <Row className="g-2">
                <Col sm="6">
                  <InfoItem title="نام">{order?.user?.firstName}</InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="نام خانوادگی">
                    {order?.user?.lastName}
                  </InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="شماره همراه">
                    {order?.user?.phoneNumber}
                  </InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="پست الکترونیک">
                    {order?.user?.email}
                  </InfoItem>
                </Col>
              </Row>
            </>
          )}
          <OrderDetails order={order} />

          <InvoiceDetails invoice={order?.invoice} />
        </ModalBody>
      </Modal>
    </>
  );
};

export default OrdersUser;

export const OrderDetails = ({ order }) => {
  return (
    <>
      <span className="text-primary border-bottom fw-bold">اطلاعات سفارش</span>
      <Row className="g-2">
        <Col sm="6">
          <InfoItem title="کدپیگیری">{order?.trackingCode}</InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="وضعیت">
            <Badge color={convertOrderStatusToColor(order?.status)}>
              {convertOrderStatusToText(order?.status)}
            </Badge>
          </InfoItem>
        </Col>
        {order?.renewalDate && (
          <Col sm="6">
            <InfoItem title="تاریخ تمدید">
              {convertDate(order?.renewalDate)}
            </InfoItem>
          </Col>
        )}
        <Col sm="6">
          <InfoItem title="تاریخ ایجاد">
            {convertDate(order?.createdAt, "YYYY/MM/DD - HH:mm")}
          </InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="تاریخ آخرین ویرایش">
            {convertDate(order?.updatedAt, "YYYY/MM/DD - HH:mm")}
          </InfoItem>
        </Col>
      </Row>
      <span className="text-primary border-bottom fw-bold">اطلاعات محصول</span>
      <Row className="g-2">
        <Col sm="6">
          <InfoItem title="عنوان">{order?.product?.title}</InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="نوع">
            {convertProductTypeToText(order?.product?.type)}
          </InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="مبلغ">
            <ConvertCurrency price={order?.product?.price} /> <Currency />
          </InfoItem>
        </Col>
        {order?.product?.fields?.map((item) => (
          <Col sm="6" key={item._id}>
            <FieldDetails item={item} details={order?.details} />
          </Col>
        ))}
        <Col xs="12">
          <InfoItem title="توضیحات">{order?.product?.description}</InfoItem>
        </Col>
      </Row>
    </>
  );
};
